/**
 * Admin — Brand Studio Hosting Manual editor.
 *
 * Singleton document. Owner / L1 / L2 can edit the title, body
 * (markdown-lite with `## ` headings), the monthly maintenance fee,
 * what it includes, and the domain disclaimer. A live preview shows
 * what the client will see (and what the PDF will look like).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, Save, Download, FileText, ArrowLeft, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['AED', 'USD', 'EUR'];

const HostingManualAdmin = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/brand/hosting-manual`, { headers });
        setDraft(r.data);
      } catch (e) {
        toast({ variant: 'destructive', title: 'Failed to load', description: e.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API_URL}/api/brand/hosting-manual`,
        {
          title_en: draft.title_en,
          content_en: draft.content_en,
          monthly_fee_amount: Number(draft.monthly_fee_amount),
          monthly_fee_currency: draft.monthly_fee_currency,
          monthly_fee_includes: draft.monthly_fee_includes,
          domain_disclaimer: draft.domain_disclaimer,
          effective_date: draft.effective_date,
        },
        { headers }
      );
      setDraft(r.data);
      toast({ title: 'Hosting Manual saved', description: 'Live across admin, PDF and public page.' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/brand/hosting-manual/pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bmp-brand-studio-hosting-manual.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Download failed', description: e.message });
    }
  };

  const addIncludeItem = () => setDraft((d) => ({ ...d, monthly_fee_includes: [...(d.monthly_fee_includes || []), ''] }));
  const updateIncludeItem = (i, v) => setDraft((d) => ({
    ...d,
    monthly_fee_includes: d.monthly_fee_includes.map((x, idx) => (idx === i ? v : x)),
  }));
  const removeIncludeItem = (i) => setDraft((d) => ({
    ...d,
    monthly_fee_includes: d.monthly_fee_includes.filter((_, idx) => idx !== i),
  }));

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading hosting manual…
      </div>
    );
  }
  if (!draft) return null;

  return (
    <div className="space-y-4" data-testid="hosting-manual-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/brand-studio" className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back to Brand Studio
          </Link>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Hosting Manual</h1>
          <p className="text-sm text-gray-500">
            Editable explainer sent to Brand Studio clients. Covers hosting on Emergent Lab,
            monthly maintenance fee and domain disclaimer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadPDF} data-testid="hosting-manual-download-pdf">
            <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0F172A] text-white" data-testid="hosting-manual-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Document Title</label>
              <input
                type="text"
                value={draft.title_en}
                onChange={(e) => setDraft({ ...draft, title_en: e.target.value })}
                className="w-full px-3 py-2 border rounded text-sm"
                data-testid="hosting-manual-title"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">
                Body Content <span className="text-gray-400 normal-case">(use `## Heading` for section titles)</span>
              </label>
              <textarea
                value={draft.content_en}
                onChange={(e) => setDraft({ ...draft, content_en: e.target.value })}
                rows={12}
                className="w-full px-3 py-2 border rounded text-sm font-mono"
                data-testid="hosting-manual-content"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Monthly Fee</label>
                <input
                  type="number"
                  step="0.01"
                  value={draft.monthly_fee_amount}
                  onChange={(e) => setDraft({ ...draft, monthly_fee_amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm"
                  data-testid="hosting-manual-fee"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Currency</label>
                <select
                  value={draft.monthly_fee_currency}
                  onChange={(e) => setDraft({ ...draft, monthly_fee_currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm bg-white"
                  data-testid="hosting-manual-currency"
                >
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-wider text-gray-500">Monthly Fee Includes</label>
                <button onClick={addIncludeItem} className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1" data-testid="hosting-manual-add-include">
                  <Plus className="w-3 h-3" /> Add item
                </button>
              </div>
              <div className="space-y-1">
                {(draft.monthly_fee_includes || []).map((item, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => updateIncludeItem(i, e.target.value)}
                      className="flex-1 px-3 py-1.5 border rounded text-sm"
                      data-testid={`hosting-manual-include-${i}`}
                    />
                    <button onClick={() => removeIncludeItem(i)} className="text-red-500 hover:text-red-700 p-1" data-testid={`hosting-manual-remove-include-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Domain Disclaimer</label>
              <textarea
                value={draft.domain_disclaimer}
                onChange={(e) => setDraft({ ...draft, domain_disclaimer: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border rounded text-sm"
                data-testid="hosting-manual-disclaimer"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Effective Date</label>
              <input
                type="date"
                value={draft.effective_date}
                onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })}
                className="px-3 py-2 border rounded text-sm"
                data-testid="hosting-manual-effective-date"
              />
            </div>
          </CardContent>
        </Card>

        {/* Live preview */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Live Preview
              </div>
              <a href="/brand-studio" target="_blank" rel="noreferrer" className="text-xs text-[#00B4D8] hover:underline inline-flex items-center gap-1">
                Public page <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="border rounded p-5 bg-white" data-testid="hosting-manual-preview">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-1">{draft.title_en || 'Untitled'}</h2>
              <div className="text-xs text-gray-500 mb-4">Effective: {draft.effective_date}</div>
              <div className="prose prose-sm max-w-none text-[#0F172A] mb-5">
                {(draft.content_en || '').split('\n\n').map((block, i) => {
                  const trimmed = block.trim();
                  if (!trimmed) return null;
                  if (trimmed.startsWith('## ')) {
                    return <h3 key={i} className="text-base font-semibold mt-4 mb-2 text-[#0F172A]">{trimmed.slice(3)}</h3>;
                  }
                  return <p key={i} className="text-sm leading-relaxed mb-2 whitespace-pre-line">{trimmed}</p>;
                })}
              </div>
              <div className="border border-[#0F172A]/20 bg-[#F1F5F9] rounded p-3 mb-3">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Monthly Maintenance Fee</div>
                <div className="text-xl font-bold text-[#0F172A]">
                  {draft.monthly_fee_currency} {Number(draft.monthly_fee_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-xs font-normal text-gray-500">/ month</span>
                </div>
                {(draft.monthly_fee_includes || []).length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {draft.monthly_fee_includes.map((it, i) => (
                      <li key={i} className="text-xs text-gray-700">• {it}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border-l-4 border-[#FF6B35] bg-[#FFF7ED] p-3 rounded-r">
                <div className="text-xs font-bold text-[#FF6B35] uppercase tracking-wider mb-1">Domain Disclaimer</div>
                <p className="text-xs text-[#7c2d12] leading-relaxed">{draft.domain_disclaimer}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostingManualAdmin;
